import { EmailCampaign } from '@spike/email-marketing-model';
import { useMasterData } from 'hooks';
import moment from 'moment';
import { FunctionComponent } from 'react';

import { humanizeCriteria } from './humanizeCriteria';

import styles from './InfoPanel.module.scss';

interface InfoPanelProps {
    campaign: EmailCampaign;
    /** Total recipient count for the campaign — from stats. */
    recipientsCount: number;
    /** ISO timestamp of when the campaign was sent — from stats. */
    sentAt: string | null;
}

/**
 * Metadata card sitting between the header and the metric cards. Lists the
 * four fields the user is most likely to want to confirm quickly: how many
 * people it went to, who they were, what the subject was, and when it was
 * sent. Card layout instead of a plain `<dl>` because the extended Figma
 * groups them visually against the pale grey background.
 */
const InfoPanel: FunctionComponent<InfoPanelProps> = ({ campaign, recipientsCount, sentAt }) => {
    const { clientTags } = useMasterData();
    const audienceLabel = humanizeCriteria(campaign, clientTags?.positive ?? []);
    const formattedSentAt = formatSentAt(sentAt);

    return (
        <dl className={styles.root}>
            <Row
                label="Recipients"
                value={recipientsCount.toLocaleString()}
            />
            <Row
                label="Audience"
                value={audienceLabel}
            />
            <Row
                label="Subject"
                value={campaign.subject || '—'}
            />
            <Row
                label="Status"
                value={
                    <span className={styles.statusValue}>
                        <span className={styles.statusBadge}>Sent</span>
                        {formattedSentAt && <span className={styles.statusDate}>{formattedSentAt}</span>}
                    </span>
                }
            />
        </dl>
    );
};

// ---------------------------------------------------------------------------
// Internal building blocks
// ---------------------------------------------------------------------------

interface RowProps {
    label: string;
    value: React.ReactNode;
}

const Row: FunctionComponent<RowProps> = ({ label, value }) => (
    <div className={styles.row}>
        <dt className={styles.label}>{label}</dt>
        <dd className={styles.value}>{value}</dd>
    </div>
);

/**
 * `MMM D, YYYY h:mm a` for the status line — matches the pattern the wizard
 * uses in the send-time review and keeps the two surfaces consistent.
 */
const formatSentAt = (iso: string | null): string => {
    if (!iso) return '';
    const m = moment(iso);
    return m.isValid() ? m.format('MMM D, YYYY h:mm a') : '';
};

export default InfoPanel;
